import {flushSync} from 'react-dom';
import {createRoot} from 'react-dom/client';
import type {ChatTranscript} from './transcript';
import {TranscriptPrintDocument} from './TranscriptPrintDocument';
import type {TranscriptPrintPalette} from './transcriptPrintStyles';
import {buildTranscriptPrintStyles} from './transcriptPrintStyles';

export const TRANSCRIPT_PRINT_FRAME_ATTRIBUTE = 'data-assistant-transcript-print';

let teardownActiveFrame: (() => void) | null = null;

export interface PrintTranscriptOptions {
  palette: TranscriptPrintPalette;
  /** Becomes the browser's default PDF file name. */
  documentTitle: string;
}

/**
 * Prints a whole transcript from an off-screen iframe so the chat pane's fixed, scrolling
 * layout cannot clip it.
 */
export const printTranscript = (
  transcript: ChatTranscript,
  {palette, documentTitle}: PrintTranscriptOptions
): void => {
  teardownActiveFrame?.();

  const frame = document.createElement('iframe');
  frame.setAttribute(TRANSCRIPT_PRINT_FRAME_ATTRIBUTE, '');
  frame.setAttribute('aria-hidden', 'true');
  frame.tabIndex = -1;
  // A display:none iframe has no layout and prints blank in some browsers.
  Object.assign(frame.style, {
    position: 'fixed',
    right: '0',
    bottom: '0',
    width: '0',
    height: '0',
    border: '0',
    visibility: 'hidden',
  });
  document.body.appendChild(frame);

  const frameWindow = frame.contentWindow;
  const frameDocument = frame.contentDocument;
  if (!frameWindow || !frameDocument) {
    frame.remove();
    throw new Error('The print view could not be created.');
  }

  // The initial about:blank document is in quirks mode; a doctype restores standards mode.
  frameDocument.open();
  frameDocument.write('<!doctype html><html><head></head><body></body></html>');
  frameDocument.close();
  frameDocument.title = documentTitle;

  const meta = frameDocument.createElement('meta');
  meta.setAttribute('charset', 'utf-8');
  frameDocument.head.appendChild(meta);
  const style = frameDocument.createElement('style');
  style.textContent = buildTranscriptPrintStyles(palette);
  frameDocument.head.appendChild(style);

  const container = frameDocument.createElement('div');
  frameDocument.body.appendChild(container);
  const root = createRoot(container);
  flushSync(() => {
    root.render(
      <TranscriptPrintDocument transcript={transcript} palette={palette} />
    );
  });

  // print() can return before the dialog closes; tearing down earlier prints a blank page.
  const teardown = () => {
    if (teardownActiveFrame === teardown) teardownActiveFrame = null;
    root.unmount();
    frame.remove();
  };
  teardownActiveFrame = teardown;
  frameWindow.addEventListener(
    'afterprint',
    () => {
      setTimeout(() => {
        if (teardownActiveFrame === teardown) teardown();
      }, 0);
    },
    {once: true}
  );

  frameWindow.focus();
  frameWindow.print();
};
